import { addDays, differenceInCalendarDays, format, parseISO, startOfWeek } from "date-fns"
import type { PoTransaction, PurchaseOrder } from "@/api/purchase_order.tsx"

export type OrderFilter = "all" | "deductions" | "due_soon" | "unpaid"

/** Orders due in this many days count as "due soon". */
export const DUE_SOON_DAYS = 14
/** An order this many days past its requested date with no payment naming it is worth a look. */
export const UNPAID_AFTER_DAYS = 45
/** Only call an order unpaid at a customer whose payments usually name their PO. */
const PAYMENT_COVERAGE_FLOOR = 0.5

/** Dollars to whole cents, so sums never drift. */
export function to_cents(amount: number | null | undefined): number {
  return Math.round((amount ?? 0) * 100)
}

export function order_date(order: PurchaseOrder): string {
  return order.po_date ?? order.document_date.slice(0, 10)
}

export function deductions_of(order: PurchaseOrder): PoTransaction[] {
  return order.transactions.filter((t: PoTransaction) => t.kind === "deduction")
}

export function payments_of(order: PurchaseOrder): PoTransaction[] {
  return order.transactions.filter((t: PoTransaction) => t.kind === "invoice")
}

/** What was deducted against an order, as a positive number of cents. */
export function deducted_cents(order: PurchaseOrder): number {
  return Math.abs(deductions_of(order).reduce((sum: number, t: PoTransaction) => sum + to_cents(t.amount), 0))
}

/** `2 Oct 2026`; nothing → an em dash. */
export function format_day(date: string | null | undefined): string {
  return date ? format(parseISO(date), "MMM d, yyyy") : "—"
}

export function format_units(units: number | null | undefined): string {
  if (units === null || units === undefined) {
    return "—"
  }
  return units.toLocaleString("en-US", { maximumFractionDigits: 2 })
}

/** The city line of a ship-to block (`AURORA, CO 80011`), which names a DC better than its code. */
export function ship_to_city(order: PurchaseOrder): string | null {
  const rows = (order.ship_to_address ?? "").split("\n").map((r: string) => r.trim()).filter((r: string) => r.length > 0)
  const city = [...rows].reverse().find((r: string) => /,\s*[A-Z]{2}\b/.test(r))
  return city ? city.replace(/\s+US$/, "").replace(/\s{2,}/g, " ") : null
}

/** A ship-to location's label: the DC's own name where it has one, then where it is. */
export function ship_to_label(order: PurchaseOrder): string {
  const city = ship_to_city(order)
  const name = (order.ship_to_name ?? "").replace(/\s*#\s*\d+$/, "").trim()
  // A bare GLN or store number, or the customer's own name again, says nothing; the city does.
  const named = name.length > 0 && !/^\d+$/.test(name) && !name.toUpperCase().startsWith(order.customer_name.toUpperCase())
  // `YORK` over `York, PA 17406` only repeats the city.
  if (named && city && city.toUpperCase().startsWith(name.toUpperCase())) {
    return city
  }
  if (named && city) {
    return `${name} · ${city}`
  }
  return city ?? (name || "Unknown")
}

export function terms_label(order: PurchaseOrder): string | null {
  if (order.terms_discount_percent !== null && order.terms_discount_days !== null && order.terms_net_days !== null) {
    return `${order.terms_discount_percent}% ${order.terms_discount_days}, net ${order.terms_net_days}`
  }
  if (order.terms_net_days !== null) {
    return `Net ${order.terms_net_days}`
  }
  return order.terms_description
}

/** Share of a customer's settled-age orders that a payment names, per customer. */
export function payment_coverage(orders: PurchaseOrder[], today: string): Map<string, { paid: number; aged: number }> {
  const coverage = new Map<string, { paid: number; aged: number }>()
  for (const order of orders) {
    if (!is_aged(order, today)) {
      continue
    }
    const row = coverage.get(order.customer_name) ?? { paid: 0, aged: 0 }
    row.aged += 1
    if (payments_of(order).length > 0) {
      row.paid += 1
    }
    coverage.set(order.customer_name, row)
  }
  return coverage
}

function is_aged(order: PurchaseOrder, today: string): boolean {
  const due = order.requested_date ?? order.po_date
  return due !== null && differenceInCalendarDays(parseISO(today), parseISO(due)) >= UNPAID_AFTER_DAYS
}

/**
 * Orders well past their requested date that no payment names — at customers whose payments
 * normally do name the PO. Without that floor every order from a customer that never prints the
 * PO on its remittance (Walmart, Albertsons) would look unpaid.
 */
export function unpaid_orders(orders: PurchaseOrder[], today: string): PurchaseOrder[] {
  const coverage = payment_coverage(orders, today)
  return orders.filter((order: PurchaseOrder) => {
    const row = coverage.get(order.customer_name)
    return (
      is_aged(order, today) &&
      payments_of(order).length === 0 &&
      row !== undefined &&
      row.aged >= 4 &&
      row.paid / row.aged >= PAYMENT_COVERAGE_FLOOR
    )
  })
}

export function due_soon(orders: PurchaseOrder[], today: string): PurchaseOrder[] {
  return orders.filter((order: PurchaseOrder) => {
    if (!order.requested_date) {
      return false
    }
    const days = differenceInCalendarDays(parseISO(order.requested_date), parseISO(today))
    return days >= 0 && days <= DUE_SOON_DAYS
  })
}

export function filter_orders(orders: PurchaseOrder[], filter: OrderFilter, today: string): PurchaseOrder[] {
  switch (filter) {
    case "deductions":
      return orders.filter((o: PurchaseOrder) => deductions_of(o).length > 0)
    case "due_soon":
      return due_soon(orders, today).sort((a: PurchaseOrder, b: PurchaseOrder) => (a.requested_date ?? "").localeCompare(b.requested_date ?? ""))
    case "unpaid":
      return unpaid_orders(orders, today)
    case "all":
      return orders
  }
}

/** Case-insensitive match on PO number, customer or ship-to. */
export function search_orders(orders: PurchaseOrder[], query: string): PurchaseOrder[] {
  const q = query.trim().toLowerCase()
  if (!q) {
    return orders
  }
  return orders.filter((o: PurchaseOrder) =>
    [o.po_number, o.customer_name, o.ship_to_address ?? ""].some((field: string) => field.toLowerCase().includes(q)),
  )
}

export interface CustomerRow {
  customer_name: string
  orders: number
  quantity: number
  amount_cents: number
  avg_lead_days: number | null
  terms: string | null
  orders_with_deductions: number
  deducted_cents: number
  paid: number
  aged: number
}

/** One row per customer, largest order value first. */
export function customer_rows(orders: PurchaseOrder[], today: string): CustomerRow[] {
  const coverage = payment_coverage(orders, today)
  const groups = new Map<string, PurchaseOrder[]>()
  for (const order of orders) {
    groups.set(order.customer_name, [...(groups.get(order.customer_name) ?? []), order])
  }
  const rows = [...groups.entries()].map(([customer_name, group]: [string, PurchaseOrder[]]): CustomerRow => {
    const leads = group.map((o: PurchaseOrder) => o.lead_days).filter((d: number | null): d is number => d !== null)
    return {
      customer_name,
      orders: group.length,
      quantity: group.reduce((sum: number, o: PurchaseOrder) => sum + (o.total_quantity ?? 0), 0),
      amount_cents: group.reduce((sum: number, o: PurchaseOrder) => sum + to_cents(o.total_amount), 0),
      avg_lead_days: leads.length ? Math.round(leads.reduce((a: number, b: number) => a + b, 0) / leads.length) : null,
      terms: most_common(group.map(terms_label)),
      orders_with_deductions: group.filter((o: PurchaseOrder) => deductions_of(o).length > 0).length,
      deducted_cents: group.reduce((sum: number, o: PurchaseOrder) => sum + deducted_cents(o), 0),
      paid: coverage.get(customer_name)?.paid ?? 0,
      aged: coverage.get(customer_name)?.aged ?? 0,
    }
  })
  return rows.sort((a: CustomerRow, b: CustomerRow) => b.amount_cents - a.amount_cents)
}

function most_common(values: (string | null)[]): string | null {
  const counts = new Map<string, number>()
  for (const value of values) {
    if (value) {
      counts.set(value, (counts.get(value) ?? 0) + 1)
    }
  }
  return [...counts.entries()].sort((a: [string, number], b: [string, number]) => b[1] - a[1])[0]?.[0] ?? null
}

export interface LocationRow {
  key: string
  customer_name: string
  label: string
  orders: number
  quantity: number
  amount_cents: number
  orders_with_deductions: number
  deducted_cents: number
}

/** One row per customer ship-to location, largest order value first. */
export function location_rows(orders: PurchaseOrder[]): LocationRow[] {
  const rows = new Map<string, LocationRow>()
  for (const order of orders) {
    const label = ship_to_label(order)
    const key = `${order.customer_name}|${label}`
    const row = rows.get(key) ?? { key, customer_name: order.customer_name, label, orders: 0, quantity: 0, amount_cents: 0, orders_with_deductions: 0, deducted_cents: 0 }
    row.orders += 1
    row.quantity += order.total_quantity ?? 0
    row.amount_cents += to_cents(order.total_amount)
    row.orders_with_deductions += deductions_of(order).length > 0 ? 1 : 0
    row.deducted_cents += deducted_cents(order)
    rows.set(key, row)
  }
  return [...rows.values()].sort((a: LocationRow, b: LocationRow) => b.amount_cents - a.amount_cents)
}

/** The series a stacked chart can carry: the largest customers by name, the rest as "Other". */
export const MAX_SERIES = 5
export const OTHER = "Other"

export function chart_series(orders: PurchaseOrder[]): string[] {
  const names = customer_rows(orders, "1970-01-01").map((r: CustomerRow) => r.customer_name)
  return names.length > MAX_SERIES ? [...names.slice(0, MAX_SERIES), OTHER] : names
}

export interface WeekRow {
  week: string
  label: string
  [series: string]: number | string
}

/** Order value in dollars per week (Monday start) and series, with empty weeks kept so gaps show. */
export function weekly_order_value(orders: PurchaseOrder[], series: string[]): WeekRow[] {
  if (orders.length === 0) {
    return []
  }
  const monday = (date: string) => format(startOfWeek(parseISO(date), { weekStartsOn: 1 }), "yyyy-MM-dd")
  const cents = new Map<string, Map<string, number>>()
  for (const order of orders) {
    const week = monday(order_date(order))
    const name = series.includes(order.customer_name) ? order.customer_name : OTHER
    const row = cents.get(week) ?? new Map<string, number>()
    row.set(name, (row.get(name) ?? 0) + to_cents(order.total_amount))
    cents.set(week, row)
  }
  const weeks = [...cents.keys()].sort()
  const rows: WeekRow[] = []
  for (let week = weeks[0]; week <= weeks[weeks.length - 1]; week = format(addDays(parseISO(week), 7), "yyyy-MM-dd")) {
    const row: WeekRow = { week, label: format(parseISO(week), "MMM d") }
    for (const name of series) {
      row[name] = (cents.get(week)?.get(name) ?? 0) / 100
    }
    rows.push(row)
  }
  return rows
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function txn(kind: PoTransaction["kind"], amount: number): PoTransaction {
    return { id: `${kind}-${amount}`, source: "kehe", kind, invoice_number: "X", amount, date: "2026-09-25", status: "new", category: null }
  }

  function order(overrides: Partial<PurchaseOrder> = {}): PurchaseOrder {
    return {
      id: "po-1",
      partner: "KeHEDistributors",
      source: "kehe",
      customer_name: "KeHE",
      po_number: "3875408",
      po_date: "2026-08-20",
      requested_date: "2026-09-04",
      requested_ship_date: null,
      purpose: "00 - Original",
      order_type: null,
      vendor_number: "02012895",
      ship_to_name: "0569813430012",
      ship_to_address: "0569813430012\nKEHE DISTRIBUTORS\n2200 N HIMALAYA ROAD\nAURORA, CO 80011",
      bill_to: null,
      fob: null,
      terms_description: "2% 10 NET 30",
      terms_discount_percent: 2,
      terms_discount_days: 10,
      terms_net_days: 30,
      total_quantity: 120,
      total_amount: 2095.2,
      allowances: [],
      s3_uri: null,
      document_date: "2026-08-20T12:00:00Z",
      line_count: 1,
      lead_days: 15,
      transactions: [],
      ...overrides,
    }
  }

  describe("purchase order display", () => {
    it("names a ship-to by its city when the first row is only a code", () => {
      expect(ship_to_label(order())).toBe("AURORA, CO 80011")
      expect(
        ship_to_label(order({ customer_name: "UNFI", ship_to_name: "ATLANTA # 9435561830005", ship_to_address: "ATLANTA # 9435561830005\n100 Lakeview Court SW\nAtlanta, GA  30336-2930" })),
      ).toBe("Atlanta, GA 30336-2930")
      expect(
        ship_to_label(order({ customer_name: "UNFI", ship_to_name: "Joliet Warehouse", ship_to_address: "Joliet Warehouse\n1 Main St\nJoliet, IL 60433" })),
      ).toBe("Joliet Warehouse · Joliet, IL 60433")
      expect(
        ship_to_label(order({ customer_name: "Wakefern", ship_to_name: "FROZEN HATFIELD # 006972418M016", ship_to_address: "FROZEN HATFIELD # 006972418M016\nHATFIELD, PA 19440" })),
      ).toBe("FROZEN HATFIELD # 006972418M016 · HATFIELD, PA 19440")
      expect(
        ship_to_label(order({ customer_name: "Kroger", ship_to_name: "KROGER (KRG LLC) # 1465226283103", ship_to_address: "KROGER (KRG LLC) # 1465226283103\nVERNON #2 FACILITY\n3420 E. VERNON AVENUE\nLOS ANGELES, CA  90058  US" })),
      ).toBe("LOS ANGELES, CA 90058")
      expect(ship_to_label(order({ ship_to_name: null, ship_to_address: null }))).toBe("Unknown")
    })

    it("sums deductions in cents and leaves payments out", () => {
      const o = order({ transactions: [txn("deduction", -41.9), txn("deduction", -20.95), txn("invoice", 2032.35)] })
      expect(deducted_cents(o)).toBe(6285)
      expect(payments_of(o)).toHaveLength(1)
    })

    it("calls an order unpaid only where the customer's payments usually name the PO", () => {
      const today = "2026-11-15"
      const paid = (id: string) => order({ id, transactions: [txn("invoice", 2000)] })
      const kehe = [paid("a"), paid("b"), paid("c"), order({ id: "missing" })]
      // Walmart never prints the PO on its remittance: no payment is not a signal there.
      const walmart = ["w1", "w2", "w3", "w4"].map((id: string) => order({ id, customer_name: "Walmart" }))
      const recent = order({ id: "recent", requested_date: "2026-11-10" })
      expect(unpaid_orders([...kehe, ...walmart, recent], today).map((o: PurchaseOrder) => o.id)).toEqual(["missing"])
    })

    it("finds orders due in the next two weeks, soonest first", () => {
      const orders = [order({ id: "late", requested_date: "2026-09-20" }), order({ id: "soon", requested_date: "2026-09-08" }), order({ id: "past", requested_date: "2026-08-30" })]
      expect(filter_orders(orders, "due_soon", "2026-09-07").map((o: PurchaseOrder) => o.id)).toEqual(["soon", "late"])
    })

    it("rolls up by customer with the deduction rate inputs", () => {
      const rows = customer_rows(
        [order({ transactions: [txn("deduction", -100)] }), order({ id: "po-2", total_amount: 1000, total_quantity: 50, lead_days: 5 }), order({ id: "po-3", customer_name: "UNFI", total_amount: 9000 })],
        "2026-09-07",
      )
      expect(rows.map((r: CustomerRow) => r.customer_name)).toEqual(["UNFI", "KeHE"])
      const kehe = rows[1]
      expect([kehe.orders, kehe.quantity, kehe.amount_cents, kehe.avg_lead_days, kehe.orders_with_deductions, kehe.deducted_cents]).toEqual([2, 170, 309520, 10, 1, 10000])
      expect(kehe.terms).toBe("2% 10, net 30")
    })

    it("charts weeks by the largest customers and keeps empty weeks", () => {
      const orders = [order(), order({ id: "po-2", po_date: "2026-09-08", total_amount: 100 }), order({ id: "po-3", customer_name: "UNFI", po_date: "2026-08-21", total_amount: 50 })]
      const series = chart_series(orders)
      expect(series).toEqual(["KeHE", "UNFI"])
      const weeks = weekly_order_value(orders, series)
      expect(weeks.map((w: WeekRow) => w.week)).toEqual(["2026-08-17", "2026-08-24", "2026-08-31", "2026-09-07"])
      expect(weeks[0]).toMatchObject({ KeHE: 2095.2, UNFI: 50 })
      expect(weeks[1]).toMatchObject({ KeHE: 0, UNFI: 0 })
      expect(weeks[3]).toMatchObject({ KeHE: 100 })
    })

    it("folds customers past the fifth into Other", () => {
      const orders = ["A", "B", "C", "D", "E", "F", "G"].map((name: string, i: number) => order({ id: name, customer_name: name, total_amount: 1000 - i }))
      expect(chart_series(orders)).toEqual(["A", "B", "C", "D", "E", OTHER])
      expect(weekly_order_value(orders, chart_series(orders))[0][OTHER]).toBe(994 + 995)
    })

    it("searches PO number, customer and ship-to", () => {
      const orders = [order(), order({ id: "po-2", po_number: "5021077", customer_name: "UNFI" })]
      expect(search_orders(orders, "5021").map((o: PurchaseOrder) => o.id)).toEqual(["po-2"])
      expect(search_orders(orders, "aurora")).toHaveLength(2)
      expect(search_orders(orders, " ")).toHaveLength(2)
    })
  })
}
